import {
  AllPointOfInterestsForDataLoaderDocument,
  PointOfInterestsDocument,
  useBulkUpdateReviewActionMutation,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useMappedFilters } from '@/hooks/useMappedFilters';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetStructureId } from '@/hooks/useGetStructureId';

export const useBulkReviewActionData = ({ poiType }: { poiType?: string }) => {
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const structureId = useGetStructureId();
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const selectedPoiIds = useAtomValue(state.selectedReviewPointsOfInterest);
  const selectedReviewPoiFilters = useAtomValue(state.selectedReviewPoiFilters);
  const selectedReviewPaintRegionIds = useAtomValue(state.selectedReviewPaintRegionIds);
  const selectedReviewWorkpackIds = useAtomValue(state.selectedReviewWorkpackIds);
  const reviewPoiSearchTerm = useAtomValue(state.reviewPoiSearchTerm);
  const selectedReviewPoiTypeId = useAtomValue(state.selectedReviewPoiTypeId);
  const selectedReviewPoiTypes = useAtomValue(state.selectedReviewPoiTypes);
  const customFilterQueryVarsState = useAtomValue(state.customFilterQueryVarsState);
  const isAllReviewPointsOfInterestSelected = useAtomValue(
    state.isAllReviewPointsOfInterestSelected
  );
  const appliedAssemblyFilters = useAtomValue(state.appliedAssemblyFilters);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);

  const [bulkUpdateReviewActionMutation, { loading }] = useBulkUpdateReviewActionMutation();

  const allActions = useMemo(() => {
    if (!allPointOfInterestTemplates) return [];
    return [
      ...new Set(
        allPointOfInterestTemplates.reduce((acc, template) => {
          if (template.fields) {
            const actionField = template.fields.find((field) => field.key === 'review_action');
            if (actionField) {
              acc.push(...actionField.options);
            }
          }
          return acc;
        }, [] as string[])
      ),
    ];
  }, [allPointOfInterestTemplates]);

  const isCurrentPoiTypeSelected = useMemo(() => {
    return selectedReviewPoiTypes.includes(poiType || '');
  }, [selectedReviewPoiTypes, poiType]);

  const templateId = useMemo(() => {
    if (!poiType && !selectedReviewPoiTypeId) return undefined;
    if (poiType) {
      return allPointOfInterestTemplates.find((template) => template.name === poiType)?.id;
    }
    return selectedReviewPoiTypeId;
  }, [allPointOfInterestTemplates, poiType, selectedReviewPoiTypeId]);

  const poiIds = useMemo(() => {
    if (isAllReviewPointsOfInterestSelected || isCurrentPoiTypeSelected) {
      return [];
    }
    return selectedPoiIds;
  }, [isAllReviewPointsOfInterestSelected, selectedPoiIds, isCurrentPoiTypeSelected]);

  const {
    selectedPoiFilters,
    isGoverningFilters,
    isVerifiedFilters,
    accessibilityFilters,
    statusFilters,
    isManuallyCreatedFilters,
    unAssignedPoiFilters,
    mediaAttachmentFilters,
    deckFilters,
  } = useMappedFilters(selectedReviewPoiFilters);

  const updateReviewAction = async (action: string) => {
    try {
      const result = await bulkUpdateReviewActionMutation({
        variables: {
          input: {
            structureId,
            action,
            ...(poiIds.length > 0 && { pointOfInterestIds: poiIds }),
            ...(poiIds.length === 0 && {
              componentIds: selectedAssemblyId ? [selectedAssemblyId] : [],
              selectedTemplateFilters: appliedAssemblyFilters.templateFilters,
              selectedDeckIds: appliedAssemblyFilters.deckFilters,
              paintRegionIds: selectedReviewPaintRegionIds,
              workpackIds: selectedReviewWorkpackIds,
              searchTerm: reviewPoiSearchTerm,
              templateId,
              selectedPoiFilters,
              isGoverningFilters,
              isVerifiedFilters,
              accessibilityFilters,
              statusFilters,
              isManuallyCreatedFilters,
              unAssignedPoiFilters,
              mediaAttachmentFilters,
              deckFilters,
              ...customFilterQueryVarsState,
            }),
          },
        },
        refetchQueries: [PointOfInterestsDocument, AllPointOfInterestsForDataLoaderDocument],
      });
      const updatedPoiCount = result.data?.bulkUpdateReviewAction?.count ?? 0;
      showSnackBar(
        `Review action updated successfully for ${updatedPoiCount} point${
          updatedPoiCount === 1 ? '' : 's'
        } of interest`,
        'success'
      );
    } catch {
      showSnackBar('Error updating review action', 'error');
    }
  };

  const disabled = useMemo(() => {
    return (
      selectedPoiIds.length === 0 &&
      !isAllReviewPointsOfInterestSelected &&
      !isCurrentPoiTypeSelected
    );
  }, [selectedPoiIds.length, isAllReviewPointsOfInterestSelected, isCurrentPoiTypeSelected]);

  const handleChange = (action: string) => {
    if (action) {
      updateReviewAction(action);
    }
  };

  return {
    allActions,
    handleChange,
    disabled,
    loading,
  };
};
